import { __assign } from "tslib";
import TextFieldWrapper, { getAccessibilityAttributes, splitProps, VARIANT } from '@mos-cat/text-field-wrapper';
import cx from 'classnames';
import * as React from 'react';
import { forwardRef, useState, useImperativeHandle, useRef } from 'react';
import './styles.scss';
/**
 * Референсная имплементация текстового поля с помощью TextFieldWrapper.
 * Имплементируя текстовые поля или расширяя функционал существующих мы обязательно должны использовать проброс референса к нативному текстововму полю, поскольку именно оно чаще всего подвергается модификациям. К тому же от текстового поля проще легко получить доступ к лейблу и произвести поиск всех связанных компонентов через `aria`-аттрибуты.
 * @param props
 * @param ref
 * @constructor
 */
var MIN_SCROLL_PADDING = 5;
function TextArea(props, ref) {
    var textareaRef = useRef(null);
    useImperativeHandle(ref, function () { return textareaRef.current; });
    var _a = useState(false), textScrolled = _a[0], setTextScrolled = _a[1];
    /**
     * Специальный хелпер помогает разделить все свойства на две части - для TextFieldWrapper и остальные
     */
    var _b = splitProps(props), tfwProps = _b.tfwProps, otherProps = _b.otherProps;
    var classNames = cx('TextField TextArea', tfwProps.className);
    /**
     * Специальный хелпер помогает сгенерировать id для aria-аттрибутов.
     */
    var a11yAttr = getAccessibilityAttributes(props.id || props.name);
    /**
     * Поле может описываться текстом из нескольких элементов.
     * Например у нас может быть лебйл ("Отзыв") (аттрибут `aria-labelledby`), описание правил ввода: ("Отзыв должен содержать не менее 400 символов, включая как минимум один китайский иероглиф, два слова на фарси, специальные символы и первые 30 знаков числа Пи") и ошибка ("Введите отзыв").
     *
     * Последнее генерируется автоматически внутри TextFieldWrapper, если ошибка является строкой и через аттрибут aria-describedby прикрепляется к текстовому полю. Чтобы не потерять все остальные описания мы собираем их в список из id.
     *
     * Если ошибка отображается в другом месте, например в поповере, мы можем пробросить его id в area-describedby и в поле передать ошибку типа `boolean`.
     */
    var maxLength = props.maxLength, _c = props.value, value = _c === void 0 ? '' : _c;
    var ariaDescribedby = [props['aria-describedby']]
        .concat(props.error && a11yAttr.describedBy)
        .filter(Boolean)
        .join(' ') || undefined;
    var count = maxLength ? maxLength - value.length : value.length;
    var moreThan90Percent = maxLength && (value.length / maxLength) * 100 > 90;
    var counterClassNames = cx('TextArea_counter', {
        warning: moreThan90Percent
    });
    var handleScroll = function () {
        if (textareaRef.current.scrollTop > MIN_SCROLL_PADDING && !textScrolled) {
            setTextScrolled(true);
        }
        if (textareaRef.current.scrollTop <= MIN_SCROLL_PADDING && textScrolled) {
            setTextScrolled(false);
        }
    };
    return (React.createElement(TextFieldWrapper, __assign({}, tfwProps, { controlId: props.id || props.name, className: classNames, suffix: maxLength ? React.createElement("span", { className: counterClassNames }, count) : null }),
        React.createElement("textarea", __assign({ ref: textareaRef }, otherProps, { onScroll: handleScroll, className: cx('TextArea_control', { underlined: textScrolled }), "aria-describedby": ariaDescribedby, "aria-labelledby": a11yAttr.labelledBy }))));
}
export default Object.assign(forwardRef(TextArea), { VARIANT: VARIANT });
export { VARIANT };
//# sourceMappingURL=TextArea.js.map